import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const TeacherMaterials = () => {
  const { user } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    file: null,
    className: '',
    subject: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        // Fetch teacher profile first
        const profileResponse = await api.get(`/teachers/user/${user?._id}`);
        
        // Fetch materials uploaded by this teacher
        const materialsResponse = await api.get(`/materials/teacher/${profileResponse.data.data._id}`);
        setMaterials(materialsResponse.data.data);
      } catch (error) {
        console.error('Error fetching materials:', error);
        // Fallback to mock data if API fails
        const mockMaterials = [
          {
            id: 1,
            title: 'Chapter 1: Algebra Basics',
            description: 'Introduction to algebraic expressions and equations',
            fileName: 'algebra_basics.pdf',
            className: 'Grade 10',
            subject: 'Mathematics',
            uploadedDate: '2023-11-15',
            fileSize: '2.4 MB',
            fileType: 'PDF'
          },
          {
            id: 2,
            title: 'Geometry Theorems',
            description: 'Proofs and applications of important geometry theorems',
            fileName: 'geometry_theorems.pdf',
            className: 'Grade 10',
            subject: 'Mathematics',
            uploadedDate: '2023-11-10',
            fileSize: '1.8 MB',
            fileType: 'PDF'
          },
          {
            id: 3,
            title: 'Practice Problems Set 1',
            description: 'Worksheet for practicing linear equations',
            fileName: 'practice_problems_set1.pdf',
            className: 'Grade 9',
            subject: 'Algebra',
            uploadedDate: '2023-11-05',
            fileSize: '1.2 MB',
            fileType: 'PDF'
          },
          {
            id: 4,
            title: 'Video Tutorial: Quadratic Equations',
            description: 'Step-by-step guide to solving quadratic equations',
            fileName: 'quadratic_equations.mp4',
            className: 'Grade 10',
            subject: 'Mathematics',
            uploadedDate: '2023-11-01',
            fileSize: '45.2 MB',
            fileType: 'MP4'
          }
        ];
        setMaterials(mockMaterials);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchMaterials();
    }
  }, [user]);

  const filteredMaterials = materials.filter(material => {
    return (!selectedClass || material.className === selectedClass) &&
           (!selectedSubject || material.subject === selectedSubject);
  });

  const uniqueClasses = [...new Set(materials.map(material => material.className))];
  const uniqueSubjects = [...new Set(materials.map(material => material.subject))];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUploadData({
      ...uploadData,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    setUploadData({
      ...uploadData,
      file: e.target.files[0]
    });
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    if (!uploadData.title || !uploadData.file || !uploadData.className || !uploadData.subject) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description);
      formData.append('file', uploadData.file);
      formData.append('className', uploadData.className);
      formData.append('subject', uploadData.subject);
      
      const response = await api.post('/materials/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Add the new material to the list
      setMaterials([response.data.data, ...materials]);
      setUploadData({
        title: '',
        description: '',
        file: null,
        className: '',
        subject: ''
      });
      setShowUploadForm(false);
      alert('Material uploaded successfully!');
    } catch (error) {
      console.error('Error uploading material:', error);
      alert('Failed to upload material. Please try again.');
    }
  };

  const handleDelete = async (materialId) => {
    if (window.confirm('Are you sure you want to delete this material?')) {
      try {
        await api.delete(`/materials/${materialId}`);
        setMaterials(materials.filter(material => material.id !== materialId));
        alert('Material deleted successfully!');
      } catch (error) {
        console.error('Error deleting material:', error);
        alert('Failed to delete material. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Study Materials</h1>
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Upload Material
          </button>
        </div>

        {showUploadForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload New Material</h2>
            <form onSubmit={handleUploadSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={uploadData.title}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={uploadData.description}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    rows="3"
                  ></textarea>
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-2">Class *</label>
                  <select
                    name="className"
                    value={uploadData.className}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="">Select Class</option>
                    {uniqueClasses.map(className => (
                      <option key={className} value={className}>{className}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-2">Subject *</label>
                  <select
                    name="subject"
                    value={uploadData.subject}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="">Select Subject</option>
                    {uniqueSubjects.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-gray-700 mb-2">File *</label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">Supported formats: PDF, DOC, DOCX, PPT, PPTX, MP4, MP3</p>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="bg-primary text-white px-6 py-2 rounded hover:bg-blue-700 transition"
                >
                  Upload
                </button>
                <button
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 mb-2">Filter by Class</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Classes</option>
                {uniqueClasses.map(className => (
                  <option key={className} value={className}>{className}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Filter by Subject</label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Subjects</option>
                {uniqueSubjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMaterials.map((material) => (
            <div key={material.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{material.title}</h3>
                  <p className="text-gray-600 text-sm mb-2">{material.description}</p>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    {material.fileName}
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{material.fileSize}</span>
                    <span>{material.uploadedDate}</span>
                  </div>
                </div>
                
                <div className="ml-4">
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    {material.fileType}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <div className="text-xs">
                  <span className="text-gray-600">{material.className}</span>
                  <span className="mx-2">•</span>
                  <span className="text-gray-600">{material.subject}</span>
                </div>
                
                <div className="flex space-x-2">
                  <button className="text-primary hover:text-blue-700 text-sm">
                    Download
                  </button>
                  <button 
                    onClick={() => handleDelete(material.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredMaterials.length === 0 && (
          <div className="text-center py-12">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No study materials found</h3>
            <p className="text-gray-500">Upload study materials for your students to access.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherMaterials;
